import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faCircleInfo,
    faReceipt,
    faUser,
    faCalendar,
    faEuroSign,
    faCreditCard,
    faTag,
    faEye,
    faDownload,
    faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

import RegisterChargePopup from "../charge/RegisterChargePopUp";
import CustomForm from "../general/form/CustomForm";
import PaginatorComponent from "../general/auxiliar/ui/PaginatorComponent";
import Collapse from "../general/auxiliar/collapse/Collapse";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import LoadingTable from "../general/auxiliar/table/LoadingTable";
import OverviewTable from "../general/auxiliar/table/OverviewTable";
import { OverviewCardList } from "../general/auxiliar/table/OverviewCard";
import TableBadge from "../general/auxiliar/table/TableBadge";
import Dropdown from "../general/auxiliar/ui/Dropdown";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../auxiliar/customHooks/usePaginationFilterEffects";
import { handleSubmit, handleSubmitWithDispatch, getTableState } from "../../auxiliar/auxFunctions";
import { getAllCashRegisters } from "../../actions/cashRegister";
import { postInvoice, downloadInvoice, sendInvoiceEmail } from "../../actions/invoice";
import { isDefined } from "../../auxiliar/formatValidators";

const CashRegisterCardList = ({
    cashRegisters,
    navigate,
    dispatch,
    activeClinicId,
    refresh,
    disabled,
}) => {
    const renderTitle = (cashRegister) => cashRegister.name;

    const renderSubtitle = (cashRegister) => (
        <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-base-content/70">
                <FontAwesomeIcon
                    icon={faUser}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm truncate">{cashRegister.name_employee}</span>
            </div>
        </div>
    );

    const renderFooter = (cashRegister) => (
        <div className="space-y-2 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCalendar}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">Servicio: {cashRegister.entity_date}</span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCalendar}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">Creación: {cashRegister.issue_date}</span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faEuroSign}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">
                    Precio: {Number(cashRegister.final_price).toFixed(2)}€
                </span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCreditCard}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">
                    <TableBadge
                        variant={Number(cashRegister.debt) > 0 ? "warning" : "success"}
                        size="sm"
                    >
                        {Number(cashRegister.debt) > 0
                            ? `${Number(cashRegister.debt).toFixed(2)}€`
                            : "Pagado"}
                    </TableBadge>
                </span>
            </div>
        </div>
    );

    const renderCardActions = (cashRegister, id) =>
        renderPatientCashRegisterDropdown(
            cashRegister,
            id,
            "xs",
            navigate,
            dispatch,
            activeClinicId,
            refresh,
            disabled,
        );

    return (
        <OverviewCardList
            data={cashRegisters}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
            renderActions={renderCardActions}
        />
    );
};

const CashRegisterTable = ({
    cashRegisters,
    navigate,
    dispatch,
    activeClinicId,
    refresh,
    disabled,
}) => {
    const columns = [
        {
            label: "Tipo",
            icon: faTag,
            width: "w-[30%]",
        },
        {
            label: (
                <>
                    Fecha{" "}
                    <div
                        className="tooltip text-md"
                        data-tip="Fecha de la sesión, clase o entrada en vigor del bono"
                    >
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </div>
                </>
            ),
            icon: faCalendar,
            width: "w-[14%]",
        },
        {
            label: (
                <>
                    Creación{" "}
                    <div className="tooltip text-md" data-tip="Fecha de creación del albarán">
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </div>
                </>
            ),
            icon: faCalendar,
            width: "w-[14%]",
        },
        {
            label: "Precio",
            icon: faEuroSign,
            width: "w-[14%]",
        },
        {
            label: "Deuda",
            icon: faCreditCard,
            width: "w-[14%]",
        },
    ];

    const getRowData = (cashRegister, id) => [
        <div
            key={`${id}-name`}
            className="font-medium text-base-content truncate"
            title={cashRegister.name}
        >
            {cashRegister.name}
        </div>,
        <div key={`${id}-date`} className="text-base-content/70">
            {cashRegister.entity_date}
        </div>,
        <div key={`${id}-date`} className="text-base-content/70">
            {cashRegister.issue_date}
        </div>,
        <div key={`${id}-price`} className="text-base-content/70">
            {Number(cashRegister.final_price).toFixed(2)}€
        </div>,
        <TableBadge
            key={`${id}-debt`}
            variant={Number(cashRegister.debt) > 0 ? "warning" : "success"}
            size="sm"
        >
            {Number(cashRegister.debt) > 0 ? `${Number(cashRegister.debt).toFixed(2)}€` : "Pagado"}
        </TableBadge>,
    ];

    const renderActions = (cashRegister, id) =>
        renderPatientCashRegisterDropdown(
            cashRegister,
            id,
            "sm",
            navigate,
            dispatch,
            activeClinicId,
            refresh,
            disabled,
        );

    return (
        <OverviewTable
            data={cashRegisters}
            columns={columns}
            getRowData={getRowData}
            renderActions={renderActions}
            actionsWidth="w-[14%]"
        />
    );
};

const OverviewCashRegistersInPatient = () => {
    const { register, watch, errors } = useFormWithDisable();
    const writtenFilter = {
        unpaid: watch("unpaid") || false,
        has_invoice: watch("has_invoice") || false,
    };

    const [refreshBool, setRefreshBool] = useState(false);
    const refresh = () => setRefreshBool(!refreshBool);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { patient_id: patientId } = useParams();
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);
    const [cashRegisters, setCashRegisters] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        per_page: 10,
        page: 1,
        clinic_id: activeClinicId,
        patient_id: patientId,
        order: "issue_date",
    });

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllCashRegisters,
        setCashRegisters,
        setFilter,
        refreshBool,
    );

    const formConfig = {
        unpaid: {
            condition: true,
            disabled: false,
            label: "Por pagar",
            info: "Márcalo para mostrar solo los albaranes por pagar",
            type: "checkbox",
            name: "unpaid",
        },
        has_invoice: {
            condition: true,
            disabled: false,
            label: "Factura asociada",
            info: "Márcalo para mostrar solo los albaranes con factura asociada",
            type: "checkbox",
            name: "has_invoice",
        },
    };

    const tableState = getTableState(cashRegisters);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando albaranes..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faReceipt}
                        title="No hay albaranes asociados"
                        description="No hay albaranes asociados a este paciente. Los albaranes se crean automáticamente al registrar sesiones, clases o bonos."
                        showButton={false}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <CashRegisterCardList
                            cashRegisters={cashRegisters}
                            navigate={navigate}
                            dispatch={dispatch}
                            activeClinicId={activeClinicId}
                            refresh={refresh}
                            disabled={disabled}
                        />
                        <CashRegisterTable
                            cashRegisters={cashRegisters}
                            navigate={navigate}
                            dispatch={dispatch}
                            activeClinicId={activeClinicId}
                            refresh={refresh}
                            disabled={disabled}
                        />
                    </>
                );
        }
    };

    return (
        <section>
            <h2 className="sr-only">Detalles de los albaranes de tu paciente en MiFisio Agenda</h2>

            <Collapse isMainTitle={true} title="Albaranes" faModality="filter">
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            {/* Pagination */}
            <div className="my-5 w-full sm:w-auto">
                <PaginatorComponent
                    pagination={cashRegisters.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                />
            </div>

            {renderContent()}

            {/* Pagination for small screens - duplicated to improve UX */}
            <div className="w-full my-5 sm:w-auto lg:hidden">
                <PaginatorComponent
                    pagination={cashRegisters.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                    showTotalItems={false}
                />
            </div>
        </section>
    );
};

// Helper function to create dropdown items for patient cash register actions
const createPatientCashRegisterDropdownItems = ({
    cashRegister,
    id,
    navigate,
    dispatch,
    activeClinicId,
    refresh,
    disabled,
}) => {
    const items = [
        {
            key: "details",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Detalles
                </div>
            ),
            onClick: () => handleSubmit(navigate, `/cash-register/${id}`)(),
        },
    ];

    // Add charge action if there's debt
    if (cashRegister.debt > 0) {
        items.push({
            key: "charge",
            custom: (
                <RegisterChargePopup
                    originalValues={cashRegister}
                    refresh={refresh}
                    trigger={
                        <div className="flex items-center w-full px-3 py-2 text-sm text-base-content cursor-pointer rounded-md transition-colors duration-150 ease-in-out hover:bg-primary/8">
                            <FontAwesomeIcon icon={faEuroSign} className="mr-2" />
                            Cobrar
                        </div>
                    }
                />
            ),
        });
    }

    // Add create invoice action if no invoice exists
    if (!isDefined(cashRegister.invoice_id)) {
        items.push({
            key: "create-invoice",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faReceipt} className="mr-2" />
                    Crear Factura
                </div>
            ),
            onClick: () =>
                handleSubmitWithDispatch(dispatch, postInvoice, activeClinicId, id, refresh)(),
            disabled,
        });
    }

    // Add invoice actions if invoice exists
    if (isDefined(cashRegister.invoice_id)) {
        items.push(
            {
                key: "download-invoice",
                label: (
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Descargar Factura
                    </div>
                ),
                onClick: () =>
                    handleSubmitWithDispatch(dispatch, downloadInvoice, cashRegister.invoice_id)(),
            },
            {
                key: "send-invoice",
                label: (
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Enviar Factura{" "}
                        <div
                            className="tooltip tooltip-left tooltip-primary ml-1 z-[100]"
                            data-tip="La factura se enviará al correo asociado al paciente"
                        >
                            <FontAwesomeIcon icon={faCircleInfo} className="text-primary" />
                        </div>
                    </div>
                ),
                onClick: () =>
                    handleSubmitWithDispatch(dispatch, sendInvoiceEmail, cashRegister.invoice_id)(),
            },
        );
    }

    return items;
};

// Shared dropdown renderer for patient cash registers
const renderPatientCashRegisterDropdown = (
    cashRegister,
    id,
    triggerSize = "sm",
    navigate,
    dispatch,
    activeClinicId,
    refresh,
    disabled,
) => {
    const dropdownItems = createPatientCashRegisterDropdownItems({
        cashRegister,
        id,
        navigate,
        dispatch,
        activeClinicId,
        refresh,
        disabled,
    });

    const trigger = (
        <div className={`btn btn-primary btn-${triggerSize}`}>
            {triggerSize === "xs" && <span className="hidden sm:inline">Opciones </span>}
            <FontAwesomeIcon icon={faChevronDown} className="text-base-100" />
            {triggerSize === "sm" && <span className="hidden xl:inline"> Opciones</span>}
        </div>
    );

    return (
        <Dropdown
            trigger={trigger}
            items={dropdownItems}
            position="bottom-right"
            width="w-52"
            closeOnItemClick={true}
        />
    );
};

export default OverviewCashRegistersInPatient;
